/* eslint-disable @typescript-eslint/no-explicit-any */
/** @jsx jsx */
import { jsx } from "@emotion/react";
import { FC, Ref } from "react";
import { InputProps as MuiInputProps, TextField } from "@material-ui/core";
import { useMaxDecimalCount } from "src/store/personalization/useSelectors";
import { useFormatMsg } from "src/hooks/useFormatMsg";
import { noop } from "../../../../globalConsts";
import CustomInputComponent, {
  CustomInputComponentProps,
} from "./CustomInputComponent";
import {
  getFormatConfigIncludingPresets,
  NumberInputFormatConfig,
  NumberInputPresetName,
} from "./inputFormat";
import { getFormattedFloatVal } from "./utils";
import useAutoCloseTooltipDebouncer from "../../AutoCloseTooltip/useAutoCloseDebounce";
import AutoCloseTooltip from "../../AutoCloseTooltip";

type ControlledOnlyProps = {
  value: string | number | null;
  defaultValue?: undefined;
  inputRef?: undefined;
};
type UncontrolledOnlyProps = {
  value?: undefined;
  defaultValue: string | number | null;
  inputRef?: Ref<any>;
};
type RefControlledOnlyProps = {
  value?: undefined;
  defaultValue?: undefined;
  inputRef: Ref<any>;
};

export type Props = (
  | ControlledOnlyProps
  | UncontrolledOnlyProps
  | RefControlledOnlyProps
) & {
  onChange?: CustomInputComponentProps["customOnChange"];
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  name?: string;
  InputProps?: Partial<MuiInputProps>;
  className?: string;
  onClick?: () => void;
  onBlur?(): void;
  id?: string;
  inputFormatConfig?: NumberInputFormatConfig;
  inputFormatPresets?: NumberInputPresetName[];
};

const FlowioNumberInput: FC<Props> = ({
  value,
  defaultValue,
  onChange = noop,
  onBlur,
  inputRef,
  placeholder,
  disabled = false,
  InputProps,
  name,
  id,
  onClick = noop,
  required = false,
  className,
  inputFormatConfig,
  inputFormatPresets,
}) => {
  const formatMsg = useFormatMsg();
  const maxDecimalCount = useMaxDecimalCount();
  const combinedFormatConfig = getFormatConfigIncludingPresets(
    inputFormatConfig,
    inputFormatPresets
  );
  const { debouncedSetTooltipVisible, showTooltip } =
    useAutoCloseTooltipDebouncer();

  const onChangeInterceptor: CustomInputComponentProps["customOnChange"] = (
    floatVal
  ) => {
    const formattedFloatVal = getFormattedFloatVal(floatVal, maxDecimalCount);
    if (floatVal !== formattedFloatVal) {
      debouncedSetTooltipVisible(true);
      onChange(formattedFloatVal);
      return;
    }

    onChange(floatVal);
  };

  return (
    <AutoCloseTooltip
      onTimerElapsed={debouncedSetTooltipVisible}
      visible={showTooltip}
      message={formatMsg("input.text.invalidDecimal", {
        max_decimal: maxDecimalCount,
      })}
    >
      <TextField
        value={value}
        required={required}
        disabled={false}
        placeholder={placeholder}
        InputProps={{
          ...InputProps,
          defaultValue,
          inputRef,
          disableUnderline: true,
          inputComponent: CustomInputComponent as FC<any>,
          inputProps: {
            ...InputProps?.inputProps,
            formatConfig: combinedFormatConfig,
            customOnChange: onChangeInterceptor,
            onBlur,
            required,
            value,
            "data-cy": "input flowio-number-input",
          },
          name,
          id,
          disabled,
        }}
        className={className}
        onClick={onClick}
      />
    </AutoCloseTooltip>
  );
};

export default FlowioNumberInput;
export type FlowioNumberInputProps = Props;
